import {
  Box,
  IconButton,
  Skeleton,
  styled,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";
import Icon from "@ui/atoms/Icon/Icon";
import { getPdfFriendlyCardStyles } from "@themes/compositions/glass";
import { buildSchemeAwareChipStyles } from "@themes/utils/gradients";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

export interface ToggleButtonOption {
  /**
   * Rendered, but not selectable. For a tab that has nothing behind it yet —
   * an Archived tab before anything has been archived — where hiding it would
   * make the control appear and disappear as the first row moves.
   */
  disabled?: boolean;
  label: React.ReactNode;
  value: string | number | boolean;
  dataTestId?: string;
  /**
   * Why this segment is unavailable, shown on hover.
   *
   * A disabled segment that says nothing leaves the reader guessing whether
   * the control is broken. MUI suppresses pointer events on a disabled
   * button, so the tooltip hangs on a wrapping span rather than the button —
   * the standard pattern, and the only one that fires here.
   */
  tooltip?: string;
  /** Optional count badge rendered inside the pill. Falsy values (incl. 0)
   * render no badge, matching the Header tabs it replaces. */
  count?: number;
}

/**
 * Wraps a segment so a DISABLED one can still explain itself.
 *
 * MUI sets `pointer-events: none` on a disabled button, which swallows hover
 * — including a tooltip hung on the button or on its label. A wrapping span
 * still receives the event, which is the documented way round it. Renders the
 * child untouched when there is no reason to give.
 */
const ToggleButtonWithReason = ({
  children,
  isInset,
  tooltip
}: {
  children: React.ReactElement;
  isInset: boolean;
  tooltip?: string;
}) =>
  tooltip ? (
    <BaseTooltip title={tooltip}>
      {/* `flex: 1` in inset mode, where the BUTTON carries it: once wrapped,
          the span is what sits in the row, so without this a segment with a
          reason shrinks to its label while its siblings stretch. */}
      <span style={{ display: "inline-flex", ...(isInset && { flex: 1 }) }}>
        {children}
      </span>
    </BaseTooltip>
  ) : (
    children
  );

export interface BaseToggleButtonsProps {
  value: string | number | boolean;
  // newValue is `any` to match MUI's ToggleButtonGroup onChange and stay
  // assignable from consumers whose handlers take a specific value type.
  // ponytail: kept loose deliberately; narrowing here breaks 4 callers for no real safety.
  handleChange: (event: React.SyntheticEvent, newValue?: any) => void;
  buttons: ToggleButtonOption[];
  loading?: boolean;
  /**
   * `"default"` is the app's tab row — filled pills, sized for navigation.
   *
   * `"inset"` is a compact segmented control that sits INSIDE another surface:
   * a recessed track with the selected segment lifted out of it in white. Used
   * where the choice belongs to the thing you are looking at rather than to the
   * page — picking a level on a credit card, for one. It stretches to fill its
   * container instead of sizing to its labels, because an inset control that is
   * narrower than the card it sits in reads as a stray button.
   */
  variant?: "default" | "inset";
}

// Count chip shown inside a pill. Mirrors Header's `TabCount` for visual parity.
const ToggleButtonCount = styled("span")(({ theme }) => ({
  ...buildSchemeAwareChipStyles(theme, theme.vars.palette.surface.base),
  alignItems: "center",
  border: `1px solid ${theme.vars.palette.border.subtle}`,
  borderRadius: theme.shape.borderRadius,
  // Explicit readable colour rather than `inherit`: on the active (filled)
  // pill the label text is white, which would vanish on the light chip.
  color: `${theme.vars.palette.text.secondary} !important`,
  display: "flex",
  flexShrink: 0,
  fontSize: theme.typography.badge.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  height: 18,
  justifyContent: "center",
  lineHeight: 1,
  minWidth: "22px",
  padding: "0 6px"
}));

const ToggleButtonLabel = styled("span")({
  alignItems: "center",
  display: "flex",
  gap: 8
});

/** How much of the visible width one arrow click travels. Less than a full page
 * so a pill straddling the edge stays partly visible and keeps its context. */
const SCROLL_STEP_RATIO = 0.75;
/** Sub-pixel layout leaves a fractional remainder at either end, which would
 * otherwise keep an arrow enabled at a position it cannot scroll past. */
const SCROLL_EPSILON_PX = 1;
/** Breathing room left beside a pill scrolled into view. */
const SCROLL_INTO_VIEW_GUTTER_PX = 8;

const ScrollViewport = styled("div")({
  "&::-webkit-scrollbar": { display: "none" },
  maxWidth: "100%",
  // `0 1 auto` (the default) with a zeroed min-width: the row sizes to its
  // content like it always has, but is now allowed to shrink below it.
  minWidth: 0,
  msOverflowStyle: "none",
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none"
});

export const BaseToggleButtons = ({
  buttons,
  handleChange,
  loading = false,
  value,
  variant = "default"
}: BaseToggleButtonsProps) => {
  const isInset = variant === "inset";
  const viewportRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    isOverflowing: false
  });

  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;
    const next = {
      canScrollLeft: viewport.scrollLeft > SCROLL_EPSILON_PX,
      canScrollRight: viewport.scrollLeft < maxScrollLeft - SCROLL_EPSILON_PX,
      isOverflowing: maxScrollLeft > SCROLL_EPSILON_PX
    };

    setScrollState((prev) =>
      prev.canScrollLeft === next.canScrollLeft &&
      prev.canScrollRight === next.canScrollRight &&
      prev.isOverflowing === next.isOverflowing
        ? prev
        : next
    );
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    measure();

    // Both boxes are observed: the viewport for container resizes (sidebar
    // opening, window resize) and the group for content resizes (labels or
    // count badges arriving after a fetch). Either alone misses half the cases.
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    if (viewport.firstElementChild) {
      observer.observe(viewport.firstElementChild);
    }
    return () => observer.disconnect();
  }, [measure, buttons, loading]);

  // A tab selected off-screen — restored from a URL, or picked before a resize
  // narrowed the row — would otherwise leave no visible sign of what is active.
  useEffect(() => {
    const viewport = viewportRef.current;
    const selected = viewport?.querySelector<HTMLElement>(".Mui-selected");
    if (!viewport || !selected) {
      return;
    }

    const viewportRect = viewport.getBoundingClientRect();
    const selectedRect = selected.getBoundingClientRect();

    if (selectedRect.left < viewportRect.left) {
      viewport.scrollLeft +=
        selectedRect.left - viewportRect.left - SCROLL_INTO_VIEW_GUTTER_PX;
    } else if (selectedRect.right > viewportRect.right) {
      viewport.scrollLeft +=
        selectedRect.right - viewportRect.right + SCROLL_INTO_VIEW_GUTTER_PX;
    }
  }, [value, buttons]);

  const scrollByStep = (direction: 1 | -1) => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const distance = viewport.clientWidth * SCROLL_STEP_RATIO * direction;
    // jsdom has no `scrollBy`; assigning `scrollLeft` works everywhere and only
    // loses the smooth animation.
    if (typeof viewport.scrollBy === "function") {
      viewport.scrollBy({ behavior: "smooth", left: distance });
    } else {
      viewport.scrollLeft += distance;
    }
  };

  const renderArrow = (direction: 1 | -1) => {
    const isLeft = direction === -1;
    return (
      <IconButton
        aria-label={isLeft ? "Scroll tabs left" : "Scroll tabs right"}
        disabled={
          isLeft ? !scrollState.canScrollLeft : !scrollState.canScrollRight
        }
        onClick={() => scrollByStep(direction)}
        size="small"
        sx={{ flexShrink: 0 }}
      >
        <Icon name={isLeft ? "chevron-left" : "chevron-right"} size="sm" />
      </IconButton>
    );
  };

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        gap: 0.5,
        // Sizes to the pills as before, but never wider than the container —
        // which is what lets the viewport shrink and the arrows appear.
        maxWidth: "100%",
        width: isInset ? "100%" : "fit-content"
      }}
    >
      {scrollState.isOverflowing ? renderArrow(-1) : null}
      <ScrollViewport
        ref={viewportRef}
        onScroll={measure}
        data-test-id="toggle-buttons-viewport"
        style={isInset ? { width: "100%" } : undefined}
      >
        <ToggleButtonGroup
          value={value}
          onChange={handleChange}
          exclusive
          sx={(theme) => ({
            ...getPdfFriendlyCardStyles(theme, theme.shape.borderRadiusButton),
            flexWrap: "nowrap",
            width: "max-content",
            ...(isInset && {
              backgroundColor: theme.vars.palette.surface.inset,
              border: `1px solid ${theme.vars.palette.border.subtle}`,
              borderRadius: `${theme.shape.borderRadiusButton}px`,
              boxShadow: "none",
              display: "flex",
              gap: "4px",
              padding: `${theme.controls.segment.inset}px`,
              width: "100%"
            })
          })}
        >
          {loading
            ? Array.from({ length: buttons.length }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  width={80}
                  sx={(theme) => ({
                    borderRadius: `${theme.controls.segment.radius}px`,
                    height: theme.controls.segment.height
                  })}
                />
              ))
            : buttons.map((button) => (
                // The active button is disabled to prevent re-selecting the
                // current value; `button.disabled` is the caller saying this
                // segment has nothing behind it. The theme styles
                // `.Mui-selected.Mui-disabled` as the resting state of the
                // former, so an UNselected disabled segment takes plain
                // `.Mui-disabled` and reads as greyed out, which is right.
                <ToggleButtonWithReason
                  key={String(button.value)}
                  isInset={isInset}
                  tooltip={button.tooltip}
                >
                  <ToggleButton
                    data-test-id={button.dataTestId}
                    value={button.value}
                    disabled={value === button.value || button.disabled}
                    size="small"
                    sx={(theme) => ({
                      /**
                       * An UNSELECTED disabled segment has to LOOK unavailable.
                       *
                       * The theme treats `.Mui-disabled` as the resting state of
                       * the selected pill (this component disables the active
                       * segment to stop it being re-selected), so it deliberately
                       * does not dim it — which left a disabled non-selected tab
                       * rendering in exactly the same colour as an enabled one:
                       * measured identical at `rgb(83, 88, 98)`. A control that
                       * looks clickable and silently does nothing is worse than
                       * one that is absent.
                       *
                       * Doubled and `:not(.Mui-selected)` so it cannot touch that
                       * resting state, here or in the inset block below.
                       */
                      "&&.Mui-disabled:not(.Mui-selected)": {
                        color: theme.vars.palette.text.disabled,
                        opacity: 0.6
                      },
                      flexShrink: 0,
                      ...(isInset && {
                        // Every selector here is doubled (`&&`). The theme's
                        // MuiToggleButton override styles `.Mui-selected` and
                        // `.Mui-selected.Mui-disabled` — and BaseToggleButtons
                        // disables the active segment, so that IS its resting
                        // state. A single `&` ties on specificity and the winner
                        // then depends on stylesheet order, which is how the
                        // first attempt ended up taking the theme's blue GRADIENT
                        // and this rule's blue TEXT: blue on blue, an unreadable
                        // pill. Doubling outranks both theme rules outright.
                        //
                        // `background`, not `backgroundColor`, for the same
                        // reason: the theme sets the shorthand, which would reset
                        // a longhand set here whatever the specificity.
                        "&&, &&.Mui-disabled": {
                          background: "transparent",
                          border: "none",
                          borderRadius: `${theme.controls.segment.radius}px`,
                          boxShadow: "none",
                          color: theme.vars.palette.text.quaternary,
                          flex: 1,
                          fontSize: theme.typography.caption.fontSize,
                          fontWeight: theme.typography.fontWeightMedium,
                          opacity: 1,
                          padding: "5px 6px",
                          textTransform: "none"
                        },
                        // The selected segment is lifted out of the track in
                        // white rather than filled with the brand colour: it sits
                        // on a card that is already tinted, and a filled pill
                        // there competes with the card's own selected state.
                        "&&.Mui-selected, &&.Mui-selected.Mui-disabled": {
                          background: theme.vars.palette.surface.overlay,
                          border: "none",
                          boxShadow: "0 1px 3px rgba(16, 20, 40, 0.14)",
                          color: theme.vars.palette.text.secondary,
                          fontWeight: theme.typography.fontWeightBold,
                          opacity: 1
                        }
                      })
                    })}
                  >
                    {button.count ? (
                      <ToggleButtonLabel>
                        {button.label}
                        <ToggleButtonCount>{button.count}</ToggleButtonCount>
                      </ToggleButtonLabel>
                    ) : (
                      button.label
                    )}
                  </ToggleButton>
                </ToggleButtonWithReason>
              ))}
        </ToggleButtonGroup>
      </ScrollViewport>
      {scrollState.isOverflowing ? renderArrow(1) : null}
    </Box>
  );
};
